
import React from 'react'
import {
  Box,
  Divider,
  Typography,
  LinearProgress,
  Tooltip,
} from '@mui/material'
// Centralized lodash imports - much cleaner!
import { isUndefined, unset } from '@/utils/lodash'
import BonusProperties from '@/housefolios-components/Properties/BonusProperties'
import { useMutation } from '@apollo/client/react'
import {
  UPDATE_USER_BY_EMAIL,
  RESET_CALCS,
} from '@/housefolios-components/ReportsComponents/mutations'
import {
  UPDATE_AFFILIATE,
  ADD_AFFILIATE,
} from '@/housefolios-components/PartnerPortal/mutations'
import { enqueueSnackbar } from 'notistack'
import { GET_AFFILIATES } from '@/housefolios-components/CreateAccount/CreateAccountCard/queries'
import { sidebarUserboxSx } from '@/layout-blueprints/chromeComponentSx'

function LimitMeter({ label, value, progress, footer  }) {
  return (
    <Box sx={{ width: '100%', alignSelf: 'stretch', textAlign: 'left' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: '0.75rem',
          width: '100%',
        }}
      >
        <Typography
          sx={{
            fontSize: 14,
            textAlign: 'left',
            lineHeight: 1.3,
            minWidth: 0,
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontSize: 12,
            opacity: 0.5,
            whiteSpace: 'nowrap',
            textAlign: 'right',
            lineHeight: 1.3,
            ml: 'auto',
          }}
        >
          {value}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={[
          {
            mt: '0.35rem',
            mb: footer ? '0.25rem' : 0,
            height: '0.3rem',
            borderRadius: '0.65rem',
            overflow: 'hidden',
          },
          {
            height: "0.3rem !important",
            "& .MuiLinearProgress-bar::after": {
              content: "\"\"",
              opacity: 0,
              position: "absolute",
              inset: 0,
              background: "#fff",
              animation: "progress-active 2s ease infinite",
            },
            background: "#f4f5fd",
            "& .MuiLinearProgress-bar": { background: "#4191ff" },
          },
        ]}
      />
      {footer}
    </Box>
  )
}

function CollapsedLimitMeters({ meters  }) {
  return (
    <Box sx={{ display: 'none', flexDirection: 'column', gap: '0.28rem', width: '100%', alignItems: 'stretch', }} data-hf="sidebar-limits-collapsed">
      {meters.map((meter) => (
        <Tooltip
          key={meter.label}
          title={
            meter.footerText
              ? `${meter.label} ${meter.value} — ${meter.footerText}`
              : `${meter.label} ${meter.value}`
          }
          placement="right"
        >
          <Box sx={{ width: '100%' }}>
            <LinearProgress
              variant="determinate"
              value={meter.progress}
              sx={{
                height: '3px !important',
                borderRadius: '2px',
                bgcolor: 'rgba(255, 255, 255, 0.12)',
                '& .MuiLinearProgress-bar': {
                  bgcolor: meter.bonus ? '#f4772e' : '#4191ff',
                },
              }}
            />
          </Box>
        </Tooltip>
      ))}
    </Box>
  )
}

const DetailsSection = ({
  session,
  LIMIT,
  marketplaceLimit,
  teamLimit,
  portfolioLimit,
  resetDate,
  daysToReset,
  teamCount,
  portfolios,
}) => {
  const [updateUserByEmail] = useMutation(UPDATE_USER_BY_EMAIL)
  const [resetCalcs] = useMutation(RESET_CALCS)
  const [updateAffiliate] = useMutation(UPDATE_AFFILIATE)
  const [addAffiliate] = useMutation(ADD_AFFILIATE)

  if (session?.me?.activeOrg?.isRealeflow) return null

  const showOrgLimits = session?.me?.activeOrg?.member !== 'RWN'
  const meters = [
    {
      label: 'Bonus Properties',
      bonus: true,
      value: session.me.activeOrg.propertyCount.bonusProperties || 0,
      progress: session.me.activeOrg.propertyCount.bonusProperties ? 100 : 0,
    },
    {
      label: 'Monthly Limit',
      value: !isUndefined(LIMIT)
        ? `${session.me.activeOrg.propertyCount.monthlyCount || 0} / ${Number(LIMIT)}`
        : 'Unlimited',
      progress: !isUndefined(LIMIT)
        ? session.me.activeOrg.propertyCount.monthlyCount > LIMIT
          ? 100
          : (session.me.activeOrg.propertyCount.monthlyCount / LIMIT) * 99.5 +
            0.5
        : 0.5,
      footer: resetDate ? (
        <Typography sx={{ fontSize: 12, color: '#4191ff', mt: '0.1rem' }}>
          Limit resets in {daysToReset} days
        </Typography>
      ) : null,
      footerText: resetDate ? `Limit resets in ${daysToReset} days` : null,
    },
    ...(showOrgLimits
      ? [
          {
            label: 'Marketplace Limit',
            value: !isUndefined(LIMIT)
              ? `${session.me.activeOrg.marketPlaces.length || 0} / ${Number(marketplaceLimit)}`
              : 'Unlimited',
            progress: !isUndefined(marketplaceLimit)
              ? session.me.activeOrg.marketPlaces.length > marketplaceLimit
                ? 100
                : (session.me.activeOrg.marketPlaces.length / marketplaceLimit) *
                    99.5 +
                  0.5
              : 0.5,
          },
          {
            label: 'Team Limit',
            value: !isUndefined(teamLimit)
              ? `${teamCount || 0} / ${teamLimit || 1}`
              : 'Unlimited',
            progress: !isUndefined(teamLimit)
              ? teamCount > teamLimit
                ? 100
                : (teamCount / teamLimit) * 99.5 + 0.5
              : 0.5,
          },
          {
            label: 'Portfolio Limit',
            value: !isUndefined(portfolioLimit)
              ? `${portfolios.length || 0} / ${portfolioLimit || 1}`
              : 'Unlimited',
            progress: !isUndefined(portfolioLimit)
              ? portfolios.length > portfolioLimit
                ? 100
                : (portfolios.length / portfolioLimit) * 99.5 + 0.5
              : 0.5,
          },
        ]
      : []),
  ]

  return (
    <>
      <Divider data-hf="sidebar-limits-divider" />
      <Box sx={sidebarUserboxSx} data-hf="sidebar-userbox">
        <CollapsedLimitMeters meters={meters} />
        <Box sx={{ width: '100%' }} data-hf="userbox-details">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: '0.75rem',
              gap: '0.25rem',
            }}
          >
            <Typography sx={{ fontSize: 14, color: '#f4772e' }}>
              Organization:
            </Typography>
            <Typography sx={{ fontSize: 14 }}>
              {session.me.activeOrg.name}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1.25rem',
              width: '100%',
              alignSelf: 'stretch',
              textAlign: 'left',
            }}
          >
            {meters.map((meter) => (
              <LimitMeter
                key={meter.label}
                label={meter.label}
                value={meter.value}
                progress={meter.progress}
                footer={meter.footer}
              />
            ))}
            <Box sx={{ mt: '0.5rem', width: '100%' }}>
              <BonusProperties session={session} sidebarMenu={true} />
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  )
}

export default DetailsSection
